import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { api } from './api';

export interface QueueStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'success' | 'error';
  error?: string;
}

interface BackfillState {
  symbol: string;
  queue: QueueStep[];
}

interface BackfillContextValue {
  backfillSymbol: string;
  queue: QueueStep[];
  isRunning: boolean;
  completedCount: number;
  errorCount: number;
  progress: number;
  startBackfill: (symbol: string) => void;
  cancel: () => void;
  dismiss: () => void;
  retryFailed: () => void;
  removeStep: (stepId: string) => void;
}

const BackfillContext = createContext<BackfillContextValue | null>(null);

export function useBackfill() {
  const ctx = useContext(BackfillContext);
  if (!ctx) throw new Error('useBackfill must be used within BackfillProvider');
  return ctx;
}

const STORAGE_KEY = 'backfill-state';

function saveBackfill(state: BackfillState | null) {
  if (state && state.queue.length > 0) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function loadBackfill(): BackfillState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const state: BackfillState = JSON.parse(raw);
    if (!state.symbol || !state.queue?.length) return null;
    return state;
  } catch {
    return null;
  }
}

export function buildBackfillQueue(symbol: string): QueueStep[] {
  const today = new Date();
  const steps: QueueStep[] = [];

  for (let i = 1; i <= 30; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const ds = d.toISOString().slice(0, 10);
    steps.push({ id: `news-${ds}`, label: `News ${ds}`, status: 'pending' });
  }

  steps.push({ id: 'trends', label: 'Trends (current)', status: 'pending' });

  for (let i = 1; i <= 30; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const ds = d.toISOString().slice(0, 10);
    steps.push({ id: `quotes-${ds}`, label: `Quote ${ds}`, status: 'pending' });
  }

  steps.push({ id: 'press-releases', label: 'Press Releases (90 days)', status: 'pending' });
  steps.push({ id: 'financial-reports', label: 'Financial Reports', status: 'pending' });
  steps.push({ id: 'predictions', label: 'Generate All Predictions', status: 'pending' });

  return steps;
}

export function BackfillProvider({ children }: { children: React.ReactNode }) {
  const [backfillSymbol, setBackfillSymbol] = useState('');
  const [queue, setQueue] = useState<QueueStep[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const cancelRef = useRef(false);
  const restoredRef = useRef(false);

  // Restore from localStorage on mount
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const saved = loadBackfill();
    if (saved) {
      const fixedQueue = saved.queue.map(s =>
        s.status === 'running' ? { ...s, status: 'pending' as const } : s
      );
      setBackfillSymbol(saved.symbol);
      setQueue(fixedQueue);
    }
  }, []);

  // Persist on every change
  useEffect(() => {
    if (queue.length > 0 && backfillSymbol) {
      saveBackfill({ symbol: backfillSymbol, queue });
    }
  }, [queue, backfillSymbol]);

  const runBackfill = useCallback(async (stepsToRun: QueueStep[], symbol: string) => {
    if (!symbol || stepsToRun.length === 0) return;
    setIsRunning(true);
    cancelRef.current = false;

    for (let i = 0; i < stepsToRun.length; i++) {
      if (cancelRef.current) break;

      const step = stepsToRun[i];
      if (step.status === 'success' || step.status === 'error') continue;

      setQueue(prev => {
        const next = prev.map(s => s.id === step.id ? { ...s, status: 'running' as const } : s);
        saveBackfill({ symbol, queue: next });
        return next;
      });

      try {
        if (step.id.startsWith('news-')) {
          const date = step.id.replace('news-', '');
          await api.backfillNews(symbol, date);
        } else if (step.id === 'trends') {
          await api.backfillTrends(symbol);
        } else if (step.id.startsWith('quotes-')) {
          const date = step.id.replace('quotes-', '');
          await api.backfillQuotes(symbol, date);
        } else if (step.id === 'press-releases') {
          await api.backfillPressReleases(symbol);
        } else if (step.id === 'financial-reports') {
          await api.backfillFinancialReports(symbol);
        } else if (step.id === 'predictions') {
          await api.backfillPredictions(symbol);
        }

        setQueue(prev => {
          const next = prev.map(s => s.id === step.id ? { ...s, status: 'success' as const } : s);
          saveBackfill({ symbol, queue: next });
          return next;
        });
      } catch (e: any) {
        setQueue(prev => {
          const next = prev.map(s => s.id === step.id ? { ...s, status: 'error' as const, error: e.message } : s);
          saveBackfill({ symbol, queue: next });
          return next;
        });
      }
    }

    setIsRunning(false);
  }, []);

  // Auto-start when queue has pending items
  useEffect(() => {
    if (queue.length > 0 && !isRunning && backfillSymbol && queue.some(s => s.status === 'pending')) {
      runBackfill(queue, backfillSymbol);
    }
  }, [queue, isRunning, backfillSymbol, runBackfill]);

  const completedCount = queue.filter(s => s.status === 'success').length;
  const errorCount = queue.filter(s => s.status === 'error').length;
  const progress = queue.length > 0 ? Math.round(((completedCount + errorCount) / queue.length) * 100) : 0;

  const startBackfill = useCallback((symbol: string) => {
    const newQueue = buildBackfillQueue(symbol);
    setBackfillSymbol(symbol);
    setQueue(newQueue);
    saveBackfill({ symbol, queue: newQueue });
  }, []);

  const cancel = useCallback(() => { cancelRef.current = true; }, []);

  const dismiss = useCallback(() => {
    setQueue([]);
    setBackfillSymbol('');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const retryFailed = useCallback(() => {
    setQueue(prev => {
      const next = prev.map(s =>
        s.status === 'error' ? { ...s, status: 'pending' as const, error: undefined } : s
      );
      saveBackfill({ symbol: backfillSymbol, queue: next });
      return next;
    });
  }, [backfillSymbol]);

  const removeStep = useCallback((stepId: string) => {
    setQueue(prev => {
      const next = prev.filter(s => s.id !== stepId);
      saveBackfill(next.length > 0 ? { symbol: backfillSymbol, queue: next } : null);
      return next;
    });
  }, [backfillSymbol]);

  return (
    <BackfillContext.Provider value={{
      backfillSymbol, queue, isRunning,
      completedCount, errorCount, progress,
      startBackfill, cancel, dismiss, retryFailed, removeStep,
    }}>
      {children}
    </BackfillContext.Provider>
  );
}
